import React, { ReactNode } from 'react'
import { Box } from '@mui/material'
import { useDashboardBuilder } from 'contexts/DashboardBuilderContext'
import { WidgetType, ComponentVariant } from '@otw/models'
import { DashboardWidgetSkeleton, useProgressiveReveal } from '@/components/adminDashboard'

interface SelectableWidgetProps {
  widgetId: WidgetType
  children: ReactNode
  sx?: any
  componentVariant?: ComponentVariant
  /** When set, skeleton + progressive reveal until this widget’s query is ready. */
  isLoading?: boolean
  loadingHeight?: number
  label?: string
}

export const SelectableWidget: React.FC<SelectableWidgetProps> = ({
  widgetId,
  children,
  sx = {},
  componentVariant,
  isLoading,
  loadingHeight = 180,
  label = 'Loading',
}) => {
  const {
    state: { isBuilderMode, selectedWidgets, hoveredWidget },
    selectWidget,
    deselectWidget,
    setHoveredWidget,
  } = useDashboardBuilder()

  const isSelected = selectedWidgets.includes(widgetId)
  const isHovered = hoveredWidget === widgetId
  const managesLoading = isLoading !== undefined
  const revealed = useProgressiveReveal(
    managesLoading ? !isLoading : true,
    managesLoading,
    managesLoading ? widgetId : undefined,
  )

  const handleClick = () => {
    if (isBuilderMode) {
      if (isSelected) {
        deselectWidget(widgetId)
      } else {
        selectWidget(widgetId, componentVariant)
      }
    }
  }

  const handleMouseEnter = () => {
    if (isBuilderMode) {
      setHoveredWidget(widgetId)
    }
  }

  const handleMouseLeave = () => {
    if (isBuilderMode) {
      setHoveredWidget(null)
    }
  }

  const content = revealed ? (
    children
  ) : (
    <DashboardWidgetSkeleton minHeight={loadingHeight} label={label} />
  )

  if (!isBuilderMode) {
    return <Box sx={sx}>{content}</Box>
  }

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        border: '2px solid',
        borderRadius: 1,
        borderColor: 'info.light',
        ...sx,
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '3px solid #1976d2',
            borderRadius: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {isHovered && !isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '2px dashed #666',
            borderRadius: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 24,
            height: 24,
            backgroundColor: '#1976d2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: 'white',
              borderRadius: '50%',
            }}
          />
        </Box>
      )}

      <Box sx={{ position: 'relative', zIndex: 0 }}>
        {content}
      </Box>
    </Box>
  )
}
